import { makeStyles, tokens } from '@fluentui/react-components';
import type { MetricsRange } from '../../api/types';
import { vt } from '../../theme';
import { niceScale, peakText, tickLabel, xLabels, type Stacked } from './model';

/** The SVG's own width; it is stretched to the box, so only the paths use it. The height is the box's. */
const W = 1000;

const useStyles = makeStyles({
  // The page scrolls inside a flex column, which would otherwise squash the plot onto its labels.
  root: { display: 'flex', flexDirection: 'column', rowGap: '6px', margin: 0, flexShrink: 0 },
  // A fixed row, or the SVG would size itself from its viewBox's aspect ratio.
  plotRow: {
    display: 'grid',
    gridTemplateColumns: '34px minmax(0, 1fr)',
    flexShrink: 0,
  },
  yAxis: { position: 'relative' },
  yTick: {
    position: 'absolute',
    left: 0,
    transform: 'translateY(-50%)',
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: '10px',
    color: vt.fg4,
  },
  plot: { position: 'relative', minWidth: 0 },
  svg: { display: 'block', width: '100%', height: '100%', overflow: 'visible' },
  xRow: { position: 'relative', height: '14px', marginLeft: '34px' },
  xTick: {
    position: 'absolute',
    top: 0,
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: '10px',
    color: vt.fg4,
    whiteSpace: 'nowrap',
  },
  foot: { display: 'flex', alignItems: 'center', columnGap: '18px', marginLeft: '34px' },
  legend: { display: 'flex', flexWrap: 'wrap', columnGap: '18px', rowGap: '4px', flexGrow: 1, listStyleType: 'none', margin: 0, padding: 0 },
  legendItem: { display: 'inline-flex', alignItems: 'center', columnGap: '6px', fontSize: '11px', color: vt.fg3 },
  swatch: { width: '10px', height: '3px', borderRadius: '1.5px', flexShrink: 0 },
  peak: { fontFamily: tokens.fontFamilyMonospace, fontSize: '10px', color: vt.fg4, whiteSpace: 'nowrap' },
  empty: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: vt.fg3,
    fontSize: '12px',
  },
});

/** One band's area between its own top and the bands below it. */
function areaPath(lower: number[], upper: number[], max: number, H: number): string {
  const n = upper.length;
  const x = (i: number) => (n <= 1 ? 0 : (i / (n - 1)) * W);
  const y = (v: number) => H - (Math.min(v, max) / max) * H;
  const top = upper.map((v, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
  const bottom = lower
    .map((v, i) => [x(i), y(v)] as const)
    .reverse()
    .map(([px, py]) => `L${px.toFixed(1)},${py.toFixed(1)}`)
    .join(' ');
  return `${top} ${bottom} Z`;
}

export interface StackedChartProps {
  chart: Stacked;
  range: MetricsRange;
  /** The plot's height in px; Overview's preview is shorter than the Traffic screen's. */
  height?: number;
  /** The band legend and peak below the time axis; Overview's preview leaves them out, as in mockup 01. */
  legend?: boolean;
}

/** Mockup 06's chart: one stacked area per device band, Mbit/s up the side, time along the bottom. */
export function StackedChart({ chart, range, height: H = 170, legend = true }: StackedChartProps) {
  const s = useStyles();
  const totals = Array.from({ length: chart.points }, (_, i) => chart.bands.reduce((a, b) => a + b.mbit[i]!, 0));
  const scale = niceScale(Math.max(0, ...totals));
  const ticks = Array.from({ length: scale.ticks + 1 }, (_, i) => i * scale.step);

  // Bottom band first, so each path starts where the one below it ended.
  let base = new Array<number>(chart.points).fill(0);
  const areas = chart.bands.map((b) => {
    const upper = base.map((v, i) => v + b.mbit[i]!);
    const d = areaPath(base, upper, scale.max, H);
    base = upper;
    return { key: b.key, color: b.color, d };
  });

  return (
    <figure className={s.root} aria-label="Throughput by device">
      <div className={s.plotRow} style={{ gridTemplateRows: `${H}px`, height: `${H}px` }} data-height={H}>
        <div className={s.yAxis} aria-hidden>
          {ticks.map((v) => (
            <span key={v} className={s.yTick} style={{ top: `${(1 - v / scale.max) * 100}%` }}>
              {v === 0 ? '0' : `${tickLabel(v)} M`}
            </span>
          ))}
        </div>
        <div className={s.plot}>
          <svg className={s.svg} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img" aria-label="Stacked Mbit/s">
            {ticks.map((v) => {
              const y = H - (v / scale.max) * H;
              return (
                <line
                  key={v}
                  x1={0}
                  x2={W}
                  y1={y}
                  y2={y}
                  stroke={vt.surface2}
                  strokeWidth={1}
                  vectorEffect="non-scaling-stroke"
                />
              );
            })}
            {areas.map((a) => (
              <path
                key={a.key}
                d={a.d}
                fill={a.color}
                fillOpacity={0.22}
                stroke={a.color}
                strokeWidth={1.25}
                vectorEffect="non-scaling-stroke"
                data-band={a.key}
              />
            ))}
          </svg>
          {chart.bands.length === 0 && <div className={s.empty}>No traffic in this range</div>}
        </div>
      </div>
      <div className={s.xRow} aria-hidden>
        {xLabels(chart, range).map((l) => (
          <span
            key={l.at}
            className={s.xTick}
            style={{ left: `${l.at * 100}%`, transform: `translateX(${l.at === 0 ? '0' : l.at === 1 ? '-100%' : '-50%'})` }}
          >
            {l.label}
          </span>
        ))}
      </div>
      {legend && (
        <figcaption className={s.foot}>
          <ul className={s.legend} aria-label="Legend">
            {chart.bands.map((b) => (
              <li key={b.key} className={s.legendItem} data-band={b.key}>
                <span className={s.swatch} style={{ backgroundColor: b.color }} aria-hidden />
                {b.name}
              </li>
            ))}
          </ul>
          <span className={s.peak} data-testid="peak">
            {peakText(chart, range)}
          </span>
        </figcaption>
      )}
    </figure>
  );
}
